import { useQuery } from '@tanstack/react-query';
import { OFT_ABI } from '../config/oft';
import { STARGATE_USDC_POOLS, INK_LZ_EID } from '../config/stargate';
import { getSourceChainPublicClient } from '../utils/getSourceChainPublicClient';

export interface StargateQuoteSendData {
  nativeFee: bigint;
  lzTokenFee: bigint;
}

/**
 * Calls `quoteSend()` on the StargateV2 USDC pool to get the messaging fee.
 * Same interface as OFT quoteSend — both built on LayerZero v2.
 *
 * @param sourceChainId - EVM chain ID of the source chain (e.g. 42161 for Arbitrum)
 * @param amount - Amount in USDC base units (6 decimals)
 */
export function useStargateQuoteSend(
  sourceChainId: number | undefined,
  amount: bigint | undefined,
) {
  return useQuery<StargateQuoteSendData>({
    queryKey: ['stargateQuoteSend', sourceChainId, amount?.toString()],
    queryFn: async () => {
      if (!sourceChainId || amount === undefined) {
        throw new Error('sourceChainId and amount are required');
      }

      const poolConfig = STARGATE_USDC_POOLS[sourceChainId];
      if (!poolConfig) {
        throw new Error(`No Stargate USDC pool for chainId ${sourceChainId}`);
      }

      const publicClient = getSourceChainPublicClient(sourceChainId);

      const sendParam = {
        dstEid: INK_LZ_EID,
        to: ('0x' + '0'.repeat(64)) as `0x${string}`, // zero address for quote
        amountLD: amount,
        minAmountLD: BigInt("0"),
        extraOptions: '0x' as `0x${string}`,
        composeMsg: '0x' as `0x${string}`,
        oftCmd: '0x' as `0x${string}`, // Taxi mode (empty)
      };

      const msgFee = await publicClient.readContract({
        address: poolConfig.poolAddress,
        abi: OFT_ABI,
        functionName: 'quoteSend',
        args: [sendParam, false],
      });

      return {
        nativeFee: msgFee.nativeFee,
        lzTokenFee: msgFee.lzTokenFee,
      };
    },
    enabled: !!sourceChainId && amount !== undefined && amount > BigInt("0"),
    staleTime: 30_000,
  });
}
